import React, { useState } from 'react';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import ForwardIcon from '@mui/icons-material/Forward';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  archiveMessage,
  copyMessage,
  liveArchiveMessage,
  liveCopyMessage,
  liveMoveMessage,
  liveTrashMessage,
  moveMessage,
  trashMessage,
} from '../api/mailbox';
import FolderTransferDialog from './FolderTransferDialog';
import FilterRuleDialog from './FilterRuleDialog';

export interface MessageActionTarget {
  /** DB message id (search / legacy) */
  id?: number | null;
  folder?: number | null;
  /** Live IMAP identity */
  account?: number;
  imap_folder?: string;
  uid?: number;
  from_address?: string;
  to_addresses?: string;
  cc_addresses?: string;
  subject?: string;
}

export function isLiveTarget(m: MessageActionTarget | null | undefined): boolean {
  return Boolean(m && m.account && m.imap_folder && m.uid != null);
}

const DRAG_MIME = 'application/x-openwebmail-message';

export function setMessageDragData(e: React.DragEvent, target: MessageActionTarget) {
  e.dataTransfer.setData(DRAG_MIME, JSON.stringify(target));
  e.dataTransfer.setData('text/plain', String(target.id ?? target.uid ?? ''));
  e.dataTransfer.effectAllowed = 'move';
}

export function readMessageDragTarget(e: React.DragEvent): MessageActionTarget | null {
  const raw = e.dataTransfer.getData(DRAG_MIME);
  if (raw) {
    try {
      return JSON.parse(raw) as MessageActionTarget;
    } catch {
      /* fall through */
    }
  }
  const id = parseInt(e.dataTransfer.getData('text/plain') || '', 10);
  if (Number.isFinite(id)) return { id };
  return null;
}

interface MessageContextMenuProps {
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  message: MessageActionTarget | null;
  afterLeave?: () => void;
}

export default function MessageContextMenu({
  anchorPosition,
  onClose,
  message,
  afterLeave,
}: MessageContextMenuProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [transferMode, setTransferMode] = useState<'move' | 'copy' | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['threads'] });
    qc.invalidateQueries({ queryKey: ['live-threads'] });
    qc.invalidateQueries({ queryKey: ['messages'] });
    qc.invalidateQueries({ queryKey: ['thread'] });
    qc.invalidateQueries({ queryKey: ['live-thread'] });
    qc.invalidateQueries({ queryKey: ['folders'] });
    qc.invalidateQueries({ queryKey: ['live-folders'] });
    qc.invalidateQueries({ queryKey: ['live-message'] });
  };

  const trashMutation = useMutation({
    mutationFn: async () => {
      if (isLiveTarget(message)) {
        await liveTrashMessage({
          account: message!.account!,
          folder: message!.imap_folder!,
          uid: message!.uid!,
        });
      } else {
        await trashMessage(message!.id!);
      }
    },
    onSuccess: () => {
      invalidate();
      onClose();
      afterLeave?.();
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async () => {
      if (isLiveTarget(message)) {
        await liveArchiveMessage({
          account: message!.account!,
          folder: message!.imap_folder!,
          uid: message!.uid!,
        });
      } else {
        await archiveMessage(message!.id!);
      }
    },
    onSuccess: () => {
      invalidate();
      onClose();
      afterLeave?.();
    },
  });

  const open = Boolean(anchorPosition && message);

  return (
    <>
      <Menu
        open={open}
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={anchorPosition || undefined}
      >
        <MenuItem
          onClick={() => {
            onClose();
            if (isLiveTarget(message)) {
              navigate(`/compose?replyAccount=${message!.account}&replyFolder=${encodeURIComponent(message!.imap_folder!)}&replyUid=${message!.uid}`);
            } else {
              navigate(`/compose?reply=${message!.id}`);
            }
          }}
        >
          <ListItemIcon>
            <ReplyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reply</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onClose();
            if (isLiveTarget(message)) {
              navigate(`/compose?forwardAccount=${message!.account}&forwardFolder=${encodeURIComponent(message!.imap_folder!)}&forwardUid=${message!.uid}`);
            } else {
              navigate(`/compose?forward=${message!.id}`);
            }
          }}
        >
          <ListItemIcon>
            <ForwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Forward</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => setTransferMode('move')}>
          <ListItemIcon>
            <DriveFileMoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Move to…</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => setTransferMode('copy')}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy to…</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setFilterOpen(true);
          }}
        >
          <ListItemIcon>
            <FilterAltIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Filter from sender…</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => archiveMutation.mutate()}>
          <ListItemIcon>
            <ArchiveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Archive</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => trashMutation.mutate()}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <FolderTransferDialog
        open={Boolean(transferMode)}
        mode={transferMode || 'move'}
        excludeFolderId={message?.folder}
        excludeImapFolder={message?.imap_folder}
        onClose={() => {
          setTransferMode(null);
          onClose();
        }}
        onSelectLive={async (dest) => {
          if (!message || !transferMode) return;
          if (isLiveTarget(message)) {
            const payload = {
              account: message.account!,
              folder: message.imap_folder!,
              uid: message.uid!,
              dest_folder: dest.imap_name,
              dest_account: dest.account,
            };
            if (transferMode === 'copy') await liveCopyMessage(payload);
            else await liveMoveMessage(payload);
          } else if (message.id) {
            const { getMessage } = await import('../api/mailbox');
            const detail = await getMessage(message.id);
            // Prefer IMAP when we have folder imap from DB-backed message
            const { getFolders } = await import('../api/mailbox');
            const folders = await getFolders({ page_size: 5000 });
            const src = folders.results.find((f) => f.id === detail.folder);
            if (src) {
              const payload = {
                account: src.account,
                folder: src.imap_name,
                uid: detail.uid,
                dest_folder: dest.imap_name,
                dest_account: dest.account,
              };
              if (transferMode === 'copy') await liveCopyMessage(payload);
              else await liveMoveMessage(payload);
            } else if (transferMode === 'copy') {
              await copyMessage(message.id, dest.id);
            } else {
              await moveMessage(message.id, dest.id);
            }
          }
          invalidate();
        }}
        onSelect={async (folderId) => {
          if (!message?.id || !transferMode) return;
          if (transferMode === 'copy') await copyMessage(message.id, folderId);
          else await moveMessage(message.id, folderId);
          invalidate();
        }}
      />

      <FilterRuleDialog
        open={filterOpen}
        onClose={() => {
          setFilterOpen(false);
          onClose();
        }}
        message={
          message
            ? {
                from_address: message.from_address || '',
                to_addresses: message.to_addresses || '',
                cc_addresses: message.cc_addresses || '',
                subject: message.subject || '',
              }
            : null
        }
      />
    </>
  );
}
